import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { AppTooltip } from "@/components/AppTooltip";
import { CatalogEntityCard } from "@/components/catalog/CatalogEntityCard";
import { GridPaginationFooter, GridPerPageSelect } from "@/components/catalog/GridPaginationControls";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { emptyLoanPaymentDraft, LoanPaymentMethodFields, type LoanPaymentDraft } from "@/components/hr/LoanPaymentMethodFields";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useGridPagination } from "@/hooks/useGridPagination";
import {
  deleteSalary,
  deleteSalaryBatchById,
  downloadSalaryBatchPdf,
  downloadSalaryPdf,
  excludeSalaryBatchById,
  finalizeSalaryBatchById,
  getSalary,
  listAllSalaries,
  listAllSalaryBatches,
  processSalaryBatch,
  regenerateSalaryBatchById,
  revokeSalaryBatchById,
  salaryDetailIsDeduction,
  salaryProcessErrorMessage,
  type SalaryBatchRecord,
  type SalaryBatchStatus,
  type SalaryRecord,
} from "@/lib/hr/salaryProcessStore";
import { can, getStoredAuthUser, type CompanyBranding } from "@/lib/authUser";
import { formatCurrency } from "@/lib/currencies/currenciesStore";
import { listDepartments, syncDepartmentsFromApi } from "@/lib/hr/departmentsStore";
import { listEmployees, syncEmployeesFromApi, type EmployeeRecord } from "@/lib/hr/employeesStore";
import { validateLoanPayment } from "@/lib/hr/loansStore";
import { payFromFinalizedSalaries, salaryPaymentsApiErrorMessage } from "@/lib/hr/salaryPaymentsStore";
import { fetchBanks, type BankOption } from "@/lib/sales/banksStore";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { endOfDay, format, isValid, parseISO, startOfDay, startOfMonth, subDays } from "date-fns";
import { motion } from "framer-motion";
import {
  CalendarRange,
  CheckCircle2,
  Eye,
  FileDown,
  Filter,
  Info,
  Layers,
  LayoutGrid,
  List,
  Loader2,
  Play,
  RefreshCw,
  Search,
  ShieldCheck,
  Trash2,
  UserMinus,
  Users,
  UserX,
  Wallet,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState, type ReactElement } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

const MONTHS = [
  { v: 1, label: "January" },
  { v: 2, label: "February" },
  { v: 3, label: "March" },
  { v: 4, label: "April" },
  { v: 5, label: "May" },
  { v: 6, label: "June" },
  { v: 7, label: "July" },
  { v: 8, label: "August" },
  { v: 9, label: "September" },
  { v: 10, label: "October" },
  { v: 11, label: "November" },
  { v: 12, label: "December" },
];

const BATCH_STATUSES: SalaryBatchStatus[] = ["draft", "processing", "review", "finalized", "paid"];

type BatchSortBy =
  | "period_latest"
  | "period_oldest"
  | "net_desc"
  | "net_asc"
  | "name_asc"
  | "name_desc"
  | "updated_latest";

function periodLabel(year: number, month: number): string {
  const m = MONTHS.find((x) => x.v === month);
  return `${m?.label ?? month} ${year}`;
}

function defaultBatchName(year: number, month: number): string {
  return `${periodLabel(year, month)} payroll`;
}

function employeeDisplayName(e: EmployeeRecord): string {
  return (e.fullName || `${e.firstName} ${e.lastName}`).trim() || "—";
}

function isActiveEmployee(e: EmployeeRecord): boolean {
  return (e.employeeStatus || "").toLowerCase() === "active";
}

function batchDisplayLabel(batch: SalaryBatchRecord): string {
  if (batch.name?.trim()) return batch.name.trim();
  return periodLabel(batch.periodYear, batch.periodMonth);
}

function periodSortKey(batch: SalaryBatchRecord): number {
  return batch.periodYear * 100 + batch.periodMonth;
}

function parseMoney(raw: string): number {
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) ? n : 0;
}

function moneyWithCurrency(amount: string | number | null | undefined): string {
  return formatCurrency(typeof amount === "string" ? parseMoney(amount) : amount ?? null, {
    symbolPosition: "left_with_space",
  });
}

function salaryStatusLabel(status: SalaryRecord["status"]): string {
  if (status === "finalized") return "Finalized";
  if (status === "paid") return "Paid";
  if (status === "processed") return "Processed";
  if (status === "excluded") return "Excluded";
  if (status === "revoked") return "Revoked";
  if (status === "pending") return "Pending";
  return status;
}

function salaryStatusClass(status: SalaryRecord["status"]): string {
  if (status === "paid") {
    return "border-emerald-500/30 bg-emerald-500/10 text-emerald-800 dark:text-emerald-200";
  }
  if (status === "finalized") {
    return "border-sky-500/30 bg-sky-500/10 text-sky-800 dark:text-sky-200";
  }
  if (status === "excluded" || status === "revoked") {
    return "border-rose-500/30 bg-rose-500/10 text-rose-800 dark:text-rose-200";
  }
  if (status === "processed") {
    return "border-blue-500/30 bg-blue-500/10 text-blue-800 dark:text-blue-200";
  }
  return "border-amber-500/30 bg-amber-500/10 text-amber-900 dark:text-amber-200";
}

function salaryCanMutate(status: SalaryRecord["status"]): boolean {
  return status !== "finalized" && status !== "paid";
}

/** Day/base lines that make up `basic_salary` (not extras on top of basic). */
function isBasicComponentTitle(title: string): boolean {
  return /^(Monthly base|Present|Off\b(?!\s*worked)|Half day(?:\s+adjustment)?|Paid leave|Hourly pay|Basic\s*salary)\b/i.test(
    title.trim(),
  );
}

function salaryDetailLines(
  salary: SalaryRecord,
  kind: "additions" | "deductions",
): Array<{ title: string; amount: number }> {
  return salary.details
    .filter((d) => {
      const amount = parseMoney(d.value);
      if (Math.abs(amount) < 0.005) return false;
      const isDeduction = salaryDetailIsDeduction(d.type);
      if (kind === "deductions") return isDeduction;
      return !isDeduction && !isBasicComponentTitle(d.title);
    })
    .map((d) => ({
      title: formatSlipDayPayTitle(d.title),
      amount: parseMoney(d.value),
    }));
}

function salaryBasic(s: SalaryRecord): number {
  const snapshot = s.setupSnapshot;
  const attendance = snapshot?.attendanceSummary ?? null;
  const val =
    snapshot?.effectiveBasic ??
    attendance?.effectiveBasic ??
    snapshot?.employeeBasicSalary ??
    parseMoney(s.basicSalaryTotal);
  return typeof val === "number" ? val : parseMoney(String(val));
}

function salaryAdditions(s: SalaryRecord): number {
  return Math.max(0, Math.round((parseMoney(s.grossMonthlyTotal) - salaryBasic(s)) * 100) / 100);
}

function salaryDeductions(s: SalaryRecord): number {
  if (s.details.length > 0) {
    const fromLines = salaryDetailLines(s, "deductions").reduce((sum, line) => sum + line.amount, 0);
    if (fromLines > 0.005) return Math.round(fromLines * 100) / 100;
  }
  return Math.max(0, parseMoney(s.grossMonthlyTotal) - parseMoney(s.actualPay || s.netPayTotal));
}

function SalaryAmountBreakdownTooltip({
  kind,
  lines,
  total,
  children,
}: {
  kind: "additions" | "deductions";
  lines: Array<{ title: string; amount: number }>;
  total: number;
  children: ReactElement;
}) {
  if (lines.length === 0 || total < 0.005) {
    return children;
  }

  return (
    <AppTooltip
      side="top"
      align="center"
      contentClassName="max-w-[17rem] p-0 overflow-hidden"
      label={
        <div className="text-left">
          <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-300 border-b border-white/10">
            {kind === "additions" ? "Additions" : "Deductions"}
          </p>
          <ul className="max-h-48 overflow-y-auto py-1">
            {lines.map((line, i) => (
              <li key={`${line.title}-${i}`} className="flex items-start justify-between gap-3 px-3 py-1 text-[11px]">
                <span className="min-w-0 leading-snug text-zinc-200">{line.title}</span>
                <span className="shrink-0 tabular-nums font-medium text-white">{moneyWithCurrency(line.amount)}</span>
              </li>
            ))}
          </ul>
          <div className="flex items-center justify-between gap-3 px-3 py-1.5 border-t border-white/10 text-[11px] font-semibold">
            <span className="text-zinc-300">Total</span>
            <span className="tabular-nums text-white">{moneyWithCurrency(total)}</span>
          </div>
        </div>
      }
    >
      {children}
    </AppTooltip>
  );
}

function formatSlipDayPayTitle(title: string): string {
  const match = title.match(/^(.+) \((\d+) days? @ ([\d.]+) × (-?[\d.]+)\)$/);
  if (!match) return title;
  const days = Number(match[2]);
  const effective = Math.round(Number(match[3]) * Number(match[4]) * 100) / 100;
  return `${match[1]} (${days} day${days === 1 ? "" : "s"} @ ${effective.toFixed(2)})`;
}

function buildSlipLineItems(
  salary: SalaryRecord,
  basicAmount?: number,
): {
  earnings: Array<{ title: string; amount: string }>;
  deductions: Array<{ title: string; amount: string }>;
} {
  let earnings = salary.details
    .filter((d) => !salaryDetailIsDeduction(d.type))
    .map((d) => ({ title: formatSlipDayPayTitle(d.title), amount: d.value }));

  const deductions = salary.details
    .filter((d) => salaryDetailIsDeduction(d.type))
    .map((d) => ({ title: formatSlipDayPayTitle(d.title), amount: d.value }));

  const basic =
    basicAmount != null && Number.isFinite(basicAmount)
      ? basicAmount
      : salaryBasic(salary);
  const hasBasicSalary = earnings.some((e) => /^basic\s*salary$/i.test(e.title.trim()));
  if (!hasBasicSalary && basic > 0.005) {
    earnings = [{ title: "Basic Salary", amount: String(basic) }, ...earnings];
  }

  if (earnings.length === 0 && basic > 0.005) {
    return {
      earnings: [{ title: "Basic Salary", amount: String(basic) }],
      deductions,
    };
  }

  return { earnings, deductions };
}

const AVATAR_TONES = [
  "bg-blue-500/15 text-blue-700 dark:text-blue-300 ring-blue-500/20",
  "bg-violet-500/15 text-violet-700 dark:text-violet-300 ring-violet-500/20",
  "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 ring-emerald-500/20",
  "bg-amber-500/15 text-amber-800 dark:text-amber-200 ring-amber-500/20",
  "bg-rose-500/15 text-rose-700 dark:text-rose-300 ring-rose-500/20",
  "bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 ring-cyan-500/20",
] as const;

function employeeInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] ?? ""}${parts[parts.length - 1][0] ?? ""}`.toUpperCase();
}

function avatarToneForId(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) hash = (hash + id.charCodeAt(i)) % AVATAR_TONES.length;
  return AVATAR_TONES[hash] ?? AVATAR_TONES[0];
}

type BatchSalaryDetailPanelProps = {
  salary: SalaryRecord | null;
  employee: EmployeeRecord | undefined;
  displayName: string;
  loading: boolean;
  pdfBusy: boolean;
  onDownloadPdf: () => void;
};

function BatchSalaryDetailPanel({
  salary,
  employee,
  displayName,
  loading,
  pdfBusy,
  onDownloadPdf,
}: BatchSalaryDetailPanelProps) {
  if (loading || !salary) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-14 text-muted-foreground">
        <Loader2 className="h-7 w-7 animate-spin text-primary/70" />
        <p className="text-xs">Loading salary slip…</p>
      </div>
    );
  }

  const payDate = salary.paymentDate
    ? parseISO(salary.paymentDate)
    : salary.processedAt
      ? parseISO(salary.processedAt)
      : null;
  const deductions = salaryDeductions(salary);
  const snapshot = salary.setupSnapshot;
  const attendance = snapshot?.attendanceSummary ?? null;
  const basicAmount =
    snapshot?.effectiveBasic ??
    attendance?.effectiveBasic ??
    snapshot?.employeeBasicSalary ??
    parseMoney(salary.basicSalaryTotal);
  const { earnings, deductions: deductLines } = buildSlipLineItems(salary, basicAmount);
  const rowCount = Math.max(earnings.length, deductLines.length, 1);
  const companyBranding: CompanyBranding | null = getStoredAuthUser()?.company_branding ?? null;
  const money = (amount: string | number | null | undefined) =>
    formatCurrency(typeof amount === "string" ? parseMoney(amount) : amount ?? null, {
      symbolPosition: "left_with_space",
    });

  const employeeMeta: Array<[string, string]> = [
    ["Employee Name", displayName],
    ["Employee ID", employee?.employeeCode || "—"],
    ["Pay Period", periodLabel(salary.periodYear, salary.periodMonth)],
    ["Pay Date", payDate && isValid(payDate) ? format(payDate, "dd MMM yyyy") : "—"],
    ["Department", employee?.departmentName || "—"],
    ["Designation", employee?.designationName || "—"],
  ];

  return (
    <div className="max-h-[min(85vh,48rem)] overflow-y-auto bg-white text-foreground">
      <div className="px-4 pt-4 pb-3 border-b border-border/40">
        <div className="flex items-start justify-between gap-4">
          <div className="shrink-0">
            {companyBranding?.logo ? (
              <img
                src={companyBranding.logo}
                alt={companyBranding.name || "Company logo"}
                className="h-14 max-w-[9rem] object-contain object-left"
              />
            ) : (
              <div className="h-14 w-[9rem] rounded border border-dashed border-border/70 flex items-center justify-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Logo
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1 text-right">
            {companyBranding?.address_line ? (
              <p className="text-[11px] text-muted-foreground mt-1 leading-snug">{companyBranding.address_line}</p>
            ) : null}
            {companyBranding?.phone ? (
              <p className="text-[11px] text-muted-foreground mt-0.5">{companyBranding.phone}</p>
            ) : null}
            {companyBranding?.email ? (
              <p className="text-[11px] text-muted-foreground mt-0.5">{companyBranding.email}</p>
            ) : null}
          </div>
        </div>
      </div>

      <div className="px-4 pt-3 pb-1 text-center">
        <h2 className="inline-block text-base font-bold uppercase tracking-[0.14em] text-foreground border-b-[1.5px] border-primary pb-1 mb-0">
          Pay Slip
        </h2>
      </div>

      <div className="px-4 pt-3 pb-4 border-b border-border/40">
        <div className="rounded-lg border border-border/50 bg-slate-50/70 px-3.5 py-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
            {employeeMeta.map(([label, value]) => (
              <div key={label} className="min-w-0 flex items-baseline gap-2 text-xs">
                <span className="font-bold text-foreground shrink-0">{label}</span>
                <span className="text-muted-foreground truncate" title={value}>
                  {value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {(() => {
        const loanBal = parseMoney(salary.loanBalance ?? "");
        if (loanBal < 0.005) return null;
        return (
          <div className="px-4 pt-3">
            <div className="rounded-md border border-orange-200 bg-orange-50/90 px-3.5 py-2.5 flex items-center justify-between gap-3">
              <p className="text-xs font-bold text-orange-900">Employee Loan Balance</p>
              <p className="text-sm font-bold tabular-nums text-orange-900">{money(loanBal)}</p>
            </div>
          </div>
        );
      })()}

      <div className="px-4 py-3">
        <p className="italic text-sm text-muted-foreground mb-2">Income Details</p>
        <div className="rounded-md border border-border/50 overflow-hidden">
          <table className="w-full text-xs border-collapse">
            <thead className="bg-muted/40">
              <tr>
                <th className="py-2 px-2 text-left font-extrabold text-foreground border-b border-r border-border/40">Earnings</th>
                <th className="py-2 px-2 text-right font-extrabold text-foreground border-b border-r border-border/40 w-[22%]">Amount</th>
                <th className="py-2 px-2 text-left font-extrabold text-foreground border-b border-r border-border/40">Deductions</th>
                <th className="py-2 px-2 text-right font-extrabold text-foreground border-b border-border/40 w-[22%]">Amount</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: rowCount }).map((_, i) => {
                const earn = earnings[i];
                const deduct = deductLines[i];
                return (
                  <tr key={i} className="border-b border-border/30 last:border-b-0">
                    <td className="py-1.5 px-2 text-muted-foreground border-r border-border/30">{earn?.title ?? ""}</td>
                    <td className="py-1.5 px-2 text-right tabular-nums text-muted-foreground border-r border-border/30">
                      {earn ? money(earn.amount) : ""}
                    </td>
                    <td className="py-1.5 px-2 text-muted-foreground border-r border-border/30">{deduct?.title ?? ""}</td>
                    <td className="py-1.5 px-2 text-right tabular-nums text-muted-foreground">
                      {deduct ? money(deduct.amount) : ""}
                    </td>
                  </tr>
                );
              })}
              <tr className="bg-muted/25">
                <td className="py-2 px-2 font-bold text-foreground border-t border-r border-border/40">Gross Earnings</td>
                <td className="py-2 px-2 text-right tabular-nums font-bold text-foreground border-t border-r border-border/40">
                  {money(salary.grossMonthlyTotal)}
                </td>
                <td className="py-2 px-2 font-bold text-foreground border-t border-r border-border/40">Total Deductions</td>
                <td className="py-2 px-2 text-right tabular-nums font-bold text-rose-700 dark:text-rose-300 border-t border-border/40">
                  {money(deductions)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="mx-4 mb-3 rounded-md border border-emerald-200 bg-emerald-50/90 px-4 py-3 flex items-center justify-between gap-3">
        <p className="text-sm font-bold text-foreground">Total Net Payable</p>
        <p className="text-lg font-bold text-emerald-700 tabular-nums">{money(salary.netPayTotal)}</p>
      </div>

      {salary.notes?.trim() ? (
        <div className="px-4 pb-3 text-xs text-muted-foreground">
          <span className="font-semibold text-foreground">Notes:</span> {salary.notes}
        </div>
      ) : null}

      <div className="px-4 py-2 text-center">
        <p className="text-[10px] italic text-muted-foreground">— This is a system-generated payslip and does not require a signature. —</p>
      </div>

      <div className="px-4 py-3 border-t border-border/50 bg-muted/15 flex justify-end">
        <Button type="button" size="sm" variant="outline" className="gap-1.5 h-8" disabled={pdfBusy} onClick={onDownloadPdf}>
          {pdfBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
          Download PDF
        </Button>
      </div>
    </div>
  );
}

function batchStatusLabel(status: SalaryBatchStatus): string {
  if (status === "draft") return "Draft";
  if (status === "processing") return "Processing";
  if (status === "review") return "In review";
  if (status === "finalized") return "Finalized";
  if (status === "paid") return "Fully paid";
  return status;
}

function batchStatusBadge(status: SalaryBatchStatus): { label: string; cls: string } {
  if (status === "paid") {
    return {
      label: batchStatusLabel(status),
      cls: "bg-emerald-600/20 text-emerald-800 dark:text-emerald-300 ring-1 ring-emerald-500/30",
    };
  }
  if (status === "finalized") {
    return { label: batchStatusLabel(status), cls: "bg-sky-500/20 text-sky-800 dark:text-sky-300" };
  }
  if (status === "processing") {
    return { label: batchStatusLabel(status), cls: "bg-blue-500/20 text-blue-700 dark:text-blue-400" };
  }
  if (status === "draft") {
    return { label: batchStatusLabel(status), cls: "bg-muted text-muted-foreground" };
  }
  return { label: batchStatusLabel(status), cls: "bg-amber-500/20 text-amber-800 dark:text-amber-300" };
}

function batchListingSummary(batch: SalaryBatchRecord): string {
  const period = periodLabel(batch.periodYear, batch.periodMonth);
  const employees = `${batch.employeesCount} employee${batch.employeesCount === 1 ? "" : "s"}`;

  if (batch.status === "paid") {
    return `${period} · ${employees} · All salaries paid`;
  }
  if (batch.status === "finalized") {
    if (batch.paidCount > 0 && batch.finalizedCount > 0) {
      return `${period} · ${employees} · ${batch.paidCount} paid · ${batch.finalizedCount} awaiting payment`;
    }
    return `${period} · ${employees} · Finalized`;
  }
  if (batch.status === "processing" || batch.status === "review") {
    return `${period} · ${employees} · ${batch.processedCount} processed${
      batch.excludedCount > 0 ? ` · ${batch.excludedCount} excluded` : ""
    }`;
  }
  return `${period} · ${employees}${batch.excludedCount > 0 ? ` · ${batch.excludedCount} excluded` : ""}`;
}

function batchAmountCaption(batch: SalaryBatchRecord): string {
  const grossDed = `Gross ${moneyWithCurrency(batch.grossPayTotal)} · Ded. ${moneyWithCurrency(batch.deductionsTotal)}`;
  if (batch.status === "paid") return `Paid total · ${grossDed}`;
  if (batch.status === "finalized" && batch.paidCount > 0) {
    return `${batch.paidCount} paid so far · ${grossDed}`;
  }
  return grossDed;
}

function batchCanMutate(status: SalaryBatchStatus): boolean {
  return status !== "finalized" && status !== "paid";
}

type PendingAction =
  | { type: "regenerate"; batch: SalaryBatchRecord }
  | { type: "finalize"; batch: SalaryBatchRecord }
  | { type: "exclude"; batch: SalaryBatchRecord }
  | { type: "revoke"; batch: SalaryBatchRecord }
  | { type: "delete"; batch: SalaryBatchRecord };

type PendingSalaryAction =
  | { type: "delete"; salary: SalaryRecord }
  | { type: "pay"; salary: SalaryRecord }
  | { type: "pay_all" };

type BatchActionButtonsProps = {
  batch: SalaryBatchRecord;
  canUpdate: boolean;
  canDelete: boolean;
  canViewPayments: boolean;
  pdfLoadingId: string | null;
  onView: () => void;
  onPayments: () => void;
  onPdf: () => void;
  onAction: (action: PendingAction) => void;
};

function BatchActionButtons({
  batch,
  canUpdate,
  canDelete,
  canViewPayments,
  pdfLoadingId,
  onView,
  onPayments,
  onPdf,
  onAction,
}: BatchActionButtonsProps) {
  const pdfBusy = pdfLoadingId === batch.salaryBatchId;
  // Fully paid batches have nothing left to pay from this action.
  const showPayments = canViewPayments && batch.status === "finalized" && batch.finalizedCount > 0;
  return (
    <div className="flex flex-wrap items-center gap-1.5 justify-end">
      <AppTooltip label="View salaries in this batch">
        <Button type="button" variant="glass" size="sm" className="gap-1.5 h-8 text-xs" onClick={onView}>
          <Eye className="w-3 h-3" />
          Salaries
        </Button>
      </AppTooltip>
      {showPayments ? (
        <AppTooltip label="Payments">
          <Button type="button" variant="glass" size="sm" className="gap-1.5 h-8 text-xs" onClick={onPayments}>
            <Wallet className="w-3 h-3" />
            Payments
          </Button>
        </AppTooltip>
      ) : null}
      <AppTooltip label="Download batch PDF">
        <Button
          type="button"
          variant="glass"
          size="sm"
          className="gap-1.5 h-8 text-xs"
          disabled={pdfBusy}
          onClick={onPdf}
        >
          {pdfBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileDown className="w-3 h-3" />}
          PDF
        </Button>
      </AppTooltip>
      {canUpdate && batchCanMutate(batch.status) ? (
        <>
          <AppTooltip label="Recalculate salaries and re-add removed employees">
            <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground hover:bg-muted/70 transition-colors" onClick={() => onAction({ type: "regenerate", batch })}>
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </AppTooltip>
          <AppTooltip label="Finalize all processed salaries in batch">
            <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0 text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 hover:bg-emerald-500/10 transition-colors" onClick={() => onAction({ type: "finalize", batch })}>
              <ShieldCheck className="w-3.5 h-3.5" />
            </Button>
          </AppTooltip>
        </>
      ) : null}
      {canDelete && batchCanMutate(batch.status) ? (
        <AppTooltip label="Delete this batch">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-rose-600 dark:text-rose-400 hover:text-rose-700 dark:hover:text-rose-300 hover:bg-rose-500/10 transition-colors"
            onClick={() => onAction({ type: "delete", batch })}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </AppTooltip>
      ) : null}
    </div>
  );
}

export default function SalaryBatches() {
  const [searchParams, setSearchParams] = useSearchParams();
  const batchViewId = (searchParams.get("batch") ?? "").trim();
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canUpdate = can(user, "salary_process_can_update");
  const canDelete = can(user, "salary_process_can_delete");
  const canViewPayments = can(user, "salary_payment_can_view_any");
  const canCreatePayments = can(user, "salary_payment_can_create");
  const [isForbidden, setIsForbidden] = useState(false);

  const [rows, setRows] = useState<SalaryBatchRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [pdfLoadingId, setPdfLoadingId] = useState<string | null>(null);

  const [query, setQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterYear, setFilterYear] = useState<number | "__all__">("__all__");
  const [filterMonth, setFilterMonth] = useState<number | "__all__">("__all__");
  const [filterStatus, setFilterStatus] = useState<string>("__all__");
  const [viewMode, setViewMode] = useState<"grid" | "list">("list");
  const [sortBy, setSortBy] = useState<BatchSortBy>("period_latest");
  const [quickDate, setQuickDate] = useState<"all" | "today" | "week" | "month">("all");

  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [reason, setReason] = useState("");

  const [processOpen, setProcessOpen] = useState(false);
  const [processMode, setProcessMode] = useState<"create" | "payments">("create");
  const [processBatchName, setProcessBatchName] = useState("");
  const [processBatchNameEdited, setProcessBatchNameEdited] = useState(false);
  const [processYear, setProcessYear] = useState(() => new Date().getFullYear());
  const [processMonth, setProcessMonth] = useState(() => new Date().getMonth() + 1);
  const [processDepartmentId, setProcessDepartmentId] = useState<string>("__all__");
  const [processEmployeeIds, setProcessEmployeeIds] = useState<string[]>([]);
  const [processEmployeeQuery, setProcessEmployeeQuery] = useState("");
  const [processLoading, setProcessLoading] = useState(false);
  const [processDataLoading, setProcessDataLoading] = useState(false);
  const [paymentDraft, setPaymentDraft] = useState<LoanPaymentDraft>(emptyLoanPaymentDraft());
  const [banks, setBanks] = useState<BankOption[]>([]);
  const [paymentSalaries, setPaymentSalaries] = useState<SalaryRecord[]>([]);

  const [batchSalaries, setBatchSalaries] = useState<SalaryRecord[]>([]);
  const [batchSalariesLoading, setBatchSalariesLoading] = useState(false);
  const [salariesQuery, setSalariesQuery] = useState("");
  const [salaryActionLoadingId, setSalaryActionLoadingId] = useState<string | null>(null);
  const [salaryPdfLoadingId, setSalaryPdfLoadingId] = useState<string | null>(null);
  const [salaryPopoverId, setSalaryPopoverId] = useState<string | null>(null);
  const [salaryPopoverDetail, setSalaryPopoverDetail] = useState<SalaryRecord | null>(null);
  const [salaryPopoverLoading, setSalaryPopoverLoading] = useState(false);
  const [pendingSalaryAction, setPendingSalaryAction] = useState<PendingSalaryAction | null>(null);

  const [empTick, setEmpTick] = useState(0);
  const employees = useMemo(() => listEmployees(), [empTick]);
  const departments = useMemo(() => listDepartments(), [empTick]);

  const filteredProcessEmployees = useMemo(() => {
    const q = processEmployeeQuery.trim().toLowerCase();
    let pool = employees.filter(isActiveEmployee);
    if (processMode === "payments") {
      const payableIds = new Set(
        paymentSalaries
          .filter((s) => s.status === "finalized")
          .map((s) => s.employeeId),
      );
      pool = employees.filter((e) => payableIds.has(e.id));
    }
    if (processDepartmentId !== "__all__") {
      pool = pool.filter((e) => e.departmentId === processDepartmentId);
    }
    if (!q) return pool;
    return pool.filter((e) => {
      const name = employeeDisplayName(e).toLowerCase();
      return (
        name.includes(q) ||
        (e.employeeCode || "").toLowerCase().includes(q) ||
        (e.departmentName || "").toLowerCase().includes(q) ||
        (e.designationName || "").toLowerCase().includes(q) ||
        (e.employeeType || "").toLowerCase().includes(q) ||
        (e.email || "").toLowerCase().includes(q)
      );
    });
  }, [employees, processEmployeeQuery, processDepartmentId, processMode, paymentSalaries]);

  const selectableProcessEmployees = filteredProcessEmployees;

  const allFilteredEmployeesSelected =
    selectableProcessEmployees.length > 0 &&
    selectableProcessEmployees.every((e) => processEmployeeIds.includes(e.id));

  const yearOptions = useMemo(() => {
    const y = new Date().getFullYear();
    return [y, y - 1, y - 2, y - 3];
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setIsForbidden(false);
    try {
      const next = await listAllSalaryBatches();
      setRows(next);
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === 403) {
        setIsForbidden(true);
        setRows([]);
        return;
      }
      toast.error(salaryProcessErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!processOpen || processBatchNameEdited) return;
    setProcessBatchName(defaultBatchName(processYear, processMonth));
  }, [processOpen, processYear, processMonth, processBatchNameEdited]);

  useEffect(() => {
    void Promise.all([syncEmployeesFromApi(), syncDepartmentsFromApi()])
      .catch(() => {})
      .finally(() => setEmpTick((t) => t + 1));
  }, []);

  const loadBatchSalaries = useCallback(async (batchId: string) => {
    setBatchSalariesLoading(true);
    try {
      const salaries = await listAllSalaries({ salaryBatchId: batchId });
      setBatchSalaries(salaries);
    } catch (e) {
      toast.error(salaryProcessErrorMessage(e));
      setBatchSalaries([]);
    } finally {
      setBatchSalariesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!batchViewId) {
      setBatchSalaries([]);
      setSalariesQuery("");
      return;
    }
    void loadBatchSalaries(batchViewId);
  }, [batchViewId, loadBatchSalaries]);

  const viewBatch = useMemo(
    () => rows.find((b) => b.salaryBatchId === batchViewId) ?? null,
    [rows, batchViewId],
  );

  const employeeById = useMemo(() => {
    const map = new Map<string, EmployeeRecord>();
    employees.forEach((e) => map.set(e.id, e));
    return map;
  }, [employees]);

  const filteredBatchSalaries = useMemo(() => {
    const q = salariesQuery.trim().toLowerCase();
    return batchSalaries.filter((s) => {
      if (!q) return true;
      const emp = employeeById.get(s.employeeId);
      const name = (s.employeeName || employeeDisplayName(emp ?? ({} as EmployeeRecord))).toLowerCase();
      return (
        name.includes(q) ||
        (emp?.employeeCode || "").toLowerCase().includes(q) ||
        (emp?.departmentName || "").toLowerCase().includes(q) ||
        (emp?.designationName || "").toLowerCase().includes(q) ||
        salaryStatusLabel(s.status).toLowerCase().includes(q)
      );
    });
  }, [batchSalaries, salariesQuery, employeeById]);

  const filteredBatchSalaryTotals = useMemo(() => {
    return filteredBatchSalaries.reduce(
      (acc, s) => {
        acc.basic += salaryBasic(s);
        acc.additions += salaryAdditions(s);
        acc.gross += parseMoney(s.grossMonthlyTotal);
        acc.deductions += salaryDeductions(s);
        acc.net += parseMoney(s.actualPay || s.netPayTotal);
        acc.netRounded5 += parseMoney(s.netPayTotal);
        return acc;
      },
      { basic: 0, additions: 0, gross: 0, deductions: 0, net: 0, netRounded5: 0 },
    );
  }, [filteredBatchSalaries]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const now = new Date();

    return rows.filter((batch) => {
      if (filterYear !== "__all__" && batch.periodYear !== filterYear) return false;
      if (filterMonth !== "__all__" && batch.periodMonth !== filterMonth) return false;
      if (filterStatus !== "__all__" && batch.status !== filterStatus) return false;

      if (quickDate !== "all") {
        const updated = batch.updatedAt ? parseISO(batch.updatedAt) : null;
        if (!updated || !isValid(updated)) return false;
        const end = endOfDay(now);
        const start =
          quickDate === "today"
            ? startOfDay(now)
            : quickDate === "week"
              ? startOfDay(subDays(now, 6))
              : startOfMonth(now);
        if (updated < start || updated > end) return false;
      }

      if (!q) return true;
      const label = batchDisplayLabel(batch).toLowerCase();
      return (
        label.includes(q) ||
        batch.batchId.toLowerCase().includes(q) ||
        periodLabel(batch.periodYear, batch.periodMonth).toLowerCase().includes(q) ||
        batchStatusLabel(batch.status).toLowerCase().includes(q)
      );
    });
  }, [rows, query, filterYear, filterMonth, filterStatus, quickDate]);

  const sorted = useMemo(() => {
    const items = [...filtered];
    items.sort((a, b) => {
      switch (sortBy) {
        case "period_oldest":
          return periodSortKey(a) - periodSortKey(b);
        case "net_desc":
          return parseMoney(b.netPayTotal) - parseMoney(a.netPayTotal);
        case "net_asc":
          return parseMoney(a.netPayTotal) - parseMoney(b.netPayTotal);
        case "name_desc":
          return batchDisplayLabel(b).localeCompare(batchDisplayLabel(a));
        case "name_asc":
          return batchDisplayLabel(a).localeCompare(batchDisplayLabel(b));
        case "updated_latest": {
          const ua = a.updatedAt ?? "";
          const ub = b.updatedAt ?? "";
          return ub.localeCompare(ua);
        }
        case "period_latest":
        default:
          return periodSortKey(b) - periodSortKey(a);
      }
    });
    return items;
  }, [filtered, sortBy]);

  const appliedFiltersCount =
    (filterYear !== "__all__" ? 1 : 0) + (filterMonth !== "__all__" ? 1 : 0) + (filterStatus !== "__all__" ? 1 : 0);

  const paginationKey = `${query}|${sortBy}|${quickDate}|${filterYear}|${filterMonth}|${filterStatus}`;

  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    totalCount,
    totalPages,
    paginatedItems: paginated,
    rangeStart,
    rangeEnd,
  } = useGridPagination(sorted, paginationKey, { defaultPageSize: 20 });

  const downloadPdf = async (batch: SalaryBatchRecord) => {
    setPdfLoadingId(batch.salaryBatchId);
    try {
      const label = batchDisplayLabel(batch).replace(/\s+/g, "-");
      await downloadSalaryBatchPdf(batch.salaryBatchId, `salary-${label}.pdf`);
      toast.success("PDF downloaded.");
    } catch (e) {
      toast.error(salaryProcessErrorMessage(e));
    } finally {
      setPdfLoadingId(null);
    }
  };

  const runPendingAction = async () => {
    if (!pendingAction) return;
    const { batch } = pendingAction;
    setActionLoading(true);
    try {
      if (pendingAction.type === "regenerate") {
        const { message, processed, skipped } = await regenerateSalaryBatchById({ salaryBatchId: batch.salaryBatchId });
        toast.success(`${message} (${processed} processed, ${skipped} skipped)`);
      } else if (pendingAction.type === "finalize") {
        const { message, finalized } = await finalizeSalaryBatchById({ salaryBatchId: batch.salaryBatchId });
        toast.success(`${message} (${finalized} finalized)`);
      } else if (pendingAction.type === "exclude") {
        const { message, excluded } = await excludeSalaryBatchById({
          salaryBatchId: batch.salaryBatchId,
          reason: reason.trim() || null,
        });
        toast.success(`${message} (${excluded} excluded)`);
      } else if (pendingAction.type === "revoke") {
        const { message, revoked, skipped } = await revokeSalaryBatchById({
          salaryBatchId: batch.salaryBatchId,
          reason: reason.trim() || null,
        });
        toast.success(`${message} (${revoked} revoked, ${skipped} skipped)`);
      } else if (pendingAction.type === "delete") {
        const { message, deleted } = await deleteSalaryBatchById({ salaryBatchId: batch.salaryBatchId });
        toast.success(`${message} (${deleted} removed)`);
      }
      setPendingAction(null);
      setReason("");
      await load();
    } catch (e) {
      toast.error(salaryProcessErrorMessage(e));
    } finally {
      setActionLoading(false);
    }
  };

  const openProcessModal = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    setProcessMode("create");
    setProcessYear(y);
    setProcessMonth(m);
    setProcessBatchName(defaultBatchName(y, m));
    setProcessBatchNameEdited(false);
    setProcessEmployeeQuery("");
    setProcessDepartmentId("__all__");
    setProcessEmployeeIds([]);
    setProcessOpen(true);
    void Promise.all([syncEmployeesFromApi(), syncDepartmentsFromApi()])
      .catch(() => {})
      .finally(() => setEmpTick((t) => t + 1));
  };

  const openPaymentsModal = (batch: SalaryBatchRecord) => {
    setProcessMode("payments");
    setProcessYear(batch.periodYear);
    setProcessMonth(batch.periodMonth);
    setProcessBatchName(batch.name?.trim() || defaultBatchName(batch.periodYear, batch.periodMonth));
    setProcessBatchNameEdited(true);
    setProcessEmployeeQuery("");
    setProcessDepartmentId("__all__");
    setProcessEmployeeIds([]);
    setPaymentDraft(emptyLoanPaymentDraft());
    setPaymentSalaries([]);
    setProcessDataLoading(true);
    setProcessOpen(true);
    void Promise.all([
      syncEmployeesFromApi(),
      syncDepartmentsFromApi(),
      fetchBanks(),
      listAllSalaries({ salaryBatchId: batch.salaryBatchId }),
    ])
      .then(([, , bankRows, salaries]) => {
        setBanks(bankRows);
        setPaymentSalaries(salaries);
      })
      .catch(() => {})
      .finally(() => {
        setEmpTick((t) => t + 1);
        setProcessDataLoading(false);
      });
  };

  const closeProcessModal = () => {
    setProcessOpen(false);
    setProcessMode("create");
    setProcessEmployeeIds([]);
    setProcessEmployeeQuery("");
    setProcessDepartmentId("__all__");
    setProcessBatchNameEdited(false);
    setPaymentDraft(emptyLoanPaymentDraft());
    setBanks([]);
    setPaymentSalaries([]);
    setProcessDataLoading(false);
  };

  const toggleProcessEmployee = (employeeId: string) => {
    setProcessEmployeeIds((prev) =>
      prev.includes(employeeId) ? prev.filter((id) => id !== employeeId) : [...prev, employeeId],
    );
  };

  const toggleAllProcessEmployees = () => {
    const filteredIds = new Set(selectableProcessEmployees.map((e) => e.id));
    if (allFilteredEmployeesSelected) {
      setProcessEmployeeIds((prev) => prev.filter((id) => !filteredIds.has(id)));
      return;
    }
    setProcessEmployeeIds((prev) => {
      const next = new Set(prev);
      selectableProcessEmployees.forEach((e) => next.add(e.id));
      return [...next];
    });
  };

  const runProcessBatch = async () => {
    if (processMode === "payments") {
      if (!canCreatePayments) {
        toast.error("You don’t have permission to record salary payments.");
        return;
      }
      const paymentError = validateLoanPayment(paymentDraft);
      if (paymentError) {
        toast.error(paymentError);
        return;
      }
      if (processEmployeeIds.length === 0) {
        toast.error("Select at least one employee.");
        return;
      }
      const salaryIds = processEmployeeIds
        .map((empId) => paymentSalaries.find((s) => s.employeeId === empId && s.status === "finalized")?.id)
        .filter((id): id is string => Boolean(id));
      if (salaryIds.length === 0) {
        toast.error("No finalized salaries selected.");
        return;
      }
      setProcessLoading(true);
      try {
        const result = await payFromFinalizedSalaries({
          salaryIds,
          paymentMethod: paymentDraft.paymentMethod,
          bankId: paymentDraft.bankId,
          referenceNo: paymentDraft.referenceNo,
          chequeNo: paymentDraft.chequeNo,
          chequeDate: paymentDraft.chequeDate,
        });
        toast.success(result.message || `Paid ${result.paid} salary payment(s).`);
        if (result.skipped > 0) {
          toast.info(`${result.skipped} already paid or skipped.`);
        }
        closeProcessModal();
        await load();
        if (batchViewId) await loadBatchSalaries(batchViewId);
      } catch (e) {
        toast.error(salaryPaymentsApiErrorMessage(e));
      } finally {
        setProcessLoading(false);
      }
      return;
    }
    if (!canUpdate) {
      toast.error("You don’t have permission to process salaries.");
      return;
    }
    const name = processBatchName.trim();
    if (!name) {
      toast.error("Enter a batch name.");
      return;
    }
    if (processEmployeeIds.length === 0) {
      toast.error("Select at least one employee.");
      return;
    }
    setProcessLoading(true);
    try {
      const { message, processed, skipped, salaryBatchId, errors } = await processSalaryBatch({
        periodYear: processYear,
        periodMonth: processMonth,
        batchName: name,
        employeeIds: processEmployeeIds,
      });
      toast.success(`${message} (${processed} processed, ${skipped} skipped)`);
      if (skipped > 0 && errors.length > 0) {
        const first = errors[0] as { employee_id?: number | string; message?: string; errors?: Record<string, string[]> };
        const fieldMsg = first?.errors
          ? Object.values(first.errors).flat().filter(Boolean)[0]
          : undefined;
        toast.error(
          fieldMsg || first?.message || `Skipped employee #${first?.employee_id ?? "?"}.`,
          { duration: 8000 },
        );
      }
      if (salaryBatchId) {
        toast.info(`Batch "${name}" created.`, { duration: 5000 });
      }
      closeProcessModal();
      await load();
    } catch (e) {
      toast.error(salaryProcessErrorMessage(e));
    } finally {
      setProcessLoading(false);
    }
  };

  const openAction = (action: PendingAction) => {
    setReason("");
    setPendingAction(action);
  };

  const openBatchSalaries = (batchId: string) => {
    setSalariesQuery("");
    setSearchParams({ batch: batchId });
    void syncEmployeesFromApi()
      .catch(() => {})
      .finally(() => setEmpTick((t) => t + 1));
  };

  const closeBatchSalaries = () => {
    setSearchParams({});
    setSalariesQuery("");
    setPendingSalaryAction(null);
    setSalaryPopoverId(null);
    setSalaryPopoverDetail(null);
    setSalaryPopoverLoading(false);
  };

  const openSalaryPopover = useCallback(async (salary: SalaryRecord) => {
    setSalaryPopoverId(salary.id);
    setSalaryPopoverLoading(true);
    setSalaryPopoverDetail(null);
    try {
      const full = await getSalary(salary.id);
      setSalaryPopoverDetail(full);
    } catch (e) {
      toast.error(salaryProcessErrorMessage(e));
      setSalaryPopoverId(null);
      setSalaryPopoverDetail(null);
    } finally {
      setSalaryPopoverLoading(false);
    }
  }, []);

  const closeSalaryPopover = useCallback(() => {
    setSalaryPopoverId(null);
    setSalaryPopoverDetail(null);
    setSalaryPopoverLoading(false);
  }, []);

  const refreshBatchView = async () => {
    if (!batchViewId) return;
    await Promise.all([load(), loadBatchSalaries(batchViewId)]);
  };

  const downloadSalaryPdfForRow = async (salary: SalaryRecord) => {
    setSalaryPdfLoadingId(salary.id);
    try {
      const slug = (salary.employeeName || `employee-${salary.employeeId}`)
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
      const month = String(salary.periodMonth).padStart(2, "0");
      await downloadSalaryPdf(salary.id, `salary-${slug || "employee"}-${month}-${salary.periodYear}.pdf`);
      toast.success("PDF downloaded.");
    } catch (e) {
      toast.error(salaryProcessErrorMessage(e));
    } finally {
      setSalaryPdfLoadingId(null);
    }
  };

  const ensurePaymentBanks = () => {
    if (banks.length > 0) return;
    void fetchBanks()
      .then(setBanks)
      .catch(() => {});
  };

  const openSalaryPayConfirm = (salary: SalaryRecord) => {
    if (!canCreatePayments) {
      toast.error("You don’t have permission to record salary payments.");
      return;
    }
    if (salary.status !== "finalized") {
      toast.info(salary.status === "paid" ? "This salary is already paid." : "Only finalized salaries can be paid.");
      return;
    }
    setPaymentDraft(emptyLoanPaymentDraft());
    ensurePaymentBanks();
    setPendingSalaryAction({ type: "pay", salary });
  };

  const openBatchPayAllConfirm = () => {
    if (!canCreatePayments) {
      toast.error("You don’t have permission to record salary payments.");
      return;
    }
    const finalizedCount = batchSalaries.filter((s) => s.status === "finalized").length;
    if (finalizedCount === 0) {
      toast.error("No finalized salaries to pay in this batch.");
      return;
    }
    setPaymentDraft(emptyLoanPaymentDraft());
    ensurePaymentBanks();
    setPendingSalaryAction({ type: "pay_all" });
  };

  const runPendingSalaryAction = async () => {
    if (!pendingSalaryAction) return;

    if (pendingSalaryAction.type === "delete") {
      setSalaryActionLoadingId(pendingSalaryAction.salary.id);
      try {
        const { message } = await deleteSalary(pendingSalaryAction.salary.id);
        toast.success(message);
        setPendingSalaryAction(null);
        await refreshBatchView();
      } catch (e) {
        toast.error(salaryProcessErrorMessage(e));
      } finally {
        setSalaryActionLoadingId(null);
      }
      return;
    }

    if (!canCreatePayments) {
      toast.error("You don’t have permission to record salary payments.");
      return;
    }
    const paymentError = validateLoanPayment(paymentDraft);
    if (paymentError) {
      toast.error(paymentError);
      return;
    }

    const salaryIds =
      pendingSalaryAction.type === "pay"
        ? [pendingSalaryAction.salary.id]
        : batchSalaries.filter((s) => s.status === "finalized").map((s) => s.id);

    if (salaryIds.length === 0) {
      toast.error("No finalized salaries selected.");
      return;
    }

    setSalaryActionLoadingId(pendingSalaryAction.type === "pay" ? pendingSalaryAction.salary.id : "__pay_all__");
    try {
      const result = await payFromFinalizedSalaries({
        salaryIds,
        paymentMethod: paymentDraft.paymentMethod,
        bankId: paymentDraft.bankId,
        referenceNo: paymentDraft.referenceNo,
        chequeNo: paymentDraft.chequeNo,
        chequeDate: paymentDraft.chequeDate,
      });
      toast.success(result.message || `Paid ${result.paid} salary payment(s).`);
      if (result.skipped > 0) {
        toast.info(`${result.skipped} already paid or skipped.`);
      }
      setPendingSalaryAction(null);
      setPaymentDraft(emptyLoanPaymentDraft());
      await refreshBatchView();
    } catch (e) {
      toast.error(salaryPaymentsApiErrorMessage(e));
    } finally {
      setSalaryActionLoadingId(null);
    }
  };

  const openSalaryAction = (action: PendingSalaryAction) => {
    setPendingSalaryAction(action);
  };

  const quickDateOptions = [
    { id: "today" as const, label: "Today", mobile: "Today" },
    { id: "week" as const, label: "Week", mobile: "7D" },
    { id: "month" as const, label: "Month", mobile: "30D" },
    { id: "all" as const, label: "All", mobile: "All" },
  ];

  const sortSelect = (
    <Select value={sortBy} onValueChange={(v) => setSortBy(v as BatchSortBy)}>
      <SelectTrigger className="glass-input h-10 w-full">
        <SelectValue placeholder="Sort by" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="period_latest">Period: Latest first</SelectItem>
        <SelectItem value="period_oldest">Period: Oldest first</SelectItem>
        <SelectItem value="net_desc">Net pay: High to low</SelectItem>
        <SelectItem value="net_asc">Net pay: Low to high</SelectItem>
        <SelectItem value="updated_latest">Recently updated</SelectItem>
        <SelectItem value="name_asc">Name: A to Z</SelectItem>
        <SelectItem value="name_desc">Name: Z to A</SelectItem>
      </SelectContent>
    </Select>
  );

  if (isForbidden) {
    return (
      <PermissionDenied
        title="Salaries access required"
        message="You don’t have permission to view salaries. Ask an administrator to grant salary_process_can_view_any."
      />
    );
  }

  return (
    <Layout title="Salaries">
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-display font-bold text-foreground">Salaries</h1>
            <p className="text-sm text-muted-foreground mt-1">Grouped payroll runs by period</p>
          </div>
          {canUpdate ? (
            <AppTooltip label="Create and process a new salary batch">
              <Button variant="premium" className="gap-2 shrink-0" onClick={openProcessModal}>
                <Play className="w-4 h-4" />
                Create Batch
              </Button>
            </AppTooltip>
          ) : null}
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by batch name, period, status…"
                className="glass-input pl-9 h-10"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <Button variant="glass" className="gap-2">
                  <Filter className="w-4 h-4" />
                  Filters
                  {appliedFiltersCount > 0 ? (
                    <span className="ml-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                      {appliedFiltersCount}
                    </span>
                  ) : null}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[92vw] sm:w-[420px] overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Filter salaries</SheetTitle>
                  <SheetDescription>Refine by period year, month, or batch status.</SheetDescription>
                </SheetHeader>
                <div className="mt-6 space-y-5">
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Year</Label>
                    <Select
                      value={filterYear === "__all__" ? "__all__" : String(filterYear)}
                      onValueChange={(v) => setFilterYear(v === "__all__" ? "__all__" : Number(v))}
                    >
                      <SelectTrigger className="glass-input h-10">
                        <SelectValue placeholder="All years" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">All years</SelectItem>
                        {yearOptions.map((y) => (
                          <SelectItem key={y} value={String(y)}>
                            {y}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Month</Label>
                    <Select
                      value={filterMonth === "__all__" ? "__all__" : String(filterMonth)}
                      onValueChange={(v) => setFilterMonth(v === "__all__" ? "__all__" : Number(v))}
                    >
                      <SelectTrigger className="glass-input h-10">
                        <SelectValue placeholder="All months" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">All months</SelectItem>
                        {MONTHS.map((m) => (
                          <SelectItem key={m.v} value={String(m.v)}>
                            {m.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Status</Label>
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                      <SelectTrigger className="glass-input h-10">
                        <SelectValue placeholder="All statuses" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">All statuses</SelectItem>
                        {BATCH_STATUSES.map((s) => (
                          <SelectItem key={s} value={s}>
                            {batchStatusLabel(s)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <SheetFooter className="mt-6 gap-2">
                  <Button
                    type="button"
                    variant="glass"
                    className="w-full"
                    onClick={() => {
                      setFilterYear("__all__");
                      setFilterMonth("__all__");
                      setFilterStatus("__all__");
                    }}
                  >
                    Reset
                  </Button>
                  <SheetClose asChild>
                    <Button type="button" variant="premium" className="w-full">
                      Apply filters
                    </Button>
                  </SheetClose>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <div className="overflow-x-auto flex-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {quickDateOptions.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id
                        ? "bg-primary/10 text-foreground font-medium"
                        : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    <span className="sm:hidden">{opt.mobile}</span>
                    <span className="hidden sm:inline">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="Grid view"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="List view"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <GridPerPageSelect id="salary-batches-page-size" pageSize={pageSize} onPageSizeChange={setPageSize} />
            <div className="w-[170px] shrink-0">{sortSelect}</div>
          </div>

          <div className="hidden md:flex items-center justify-between gap-3">
            <GridPerPageSelect id="salary-batches-page-size-desktop" pageSize={pageSize} onPageSizeChange={setPageSize} />
            <div className="flex items-center gap-3 justify-end min-w-0">
              <div className="w-[190px] shrink-0">{sortSelect}</div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {quickDateOptions.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id
                        ? "bg-primary/10 text-foreground font-medium"
                        : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
                <button
                  type="button"
                  onClick={() => setViewMode("grid")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="Grid view"
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="List view"
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="glass-card p-6 text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading salaries…
          </div>
        ) : null}

        {!loading && filtered.length === 0 ? (
          <div className="glass-card p-6 text-sm text-muted-foreground">
            {rows.length === 0
              ? canUpdate
                ? "No salaries yet. Click “Create Batch” to create one."
                : "No salaries yet."
              : "No salaries match your search."}
          </div>
        ) : null}

        {!loading && filtered.length > 0 && viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {paginated.map((batch, i) => {
              const badge = batchStatusBadge(batch.status);
              return (
                <motion.div
                  key={batch.salaryBatchId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03 }}
                  className="h-full"
                >
                  <CatalogEntityCard
                    className="h-full flex flex-col"
                    title={batchDisplayLabel(batch)}
                    titleIcon={Layers}
                    statusBadge={{ label: badge.label, className: badge.cls }}
                    primaryMeta={{
                      label: "Period",
                      value: periodLabel(batch.periodYear, batch.periodMonth),
                      icon: CalendarRange,
                      iconClassName: "text-emerald-600",
                    }}
                    secondaryMeta={{
                      label: batch.status === "paid" ? "Payment" : "Employees",
                      value:
                        batch.status === "paid"
                          ? "Fully paid"
                          : batch.status === "finalized"
                            ? batch.paidCount > 0
                              ? `${batch.paidCount} paid · ${batch.finalizedCount} due`
                              : `${batch.employeesCount} finalized`
                            : `${batch.processedCount}/${batch.employeesCount} processed`,
                      icon: batch.status === "paid" ? Wallet : Users,
                      iconClassName: batch.status === "paid" ? "text-emerald-600" : "text-blue-600",
                    }}
                    footer={batchAmountCaption(batch)}
                    footerRight={moneyWithCurrency(batch.netPayTotal)}
                    footerRightClassName="text-emerald-700 dark:text-emerald-300 font-semibold"
                    onView={() => openBatchSalaries(batch.salaryBatchId)}
                    onDelete={canDelete && batchCanMutate(batch.status) ? () => openAction({ type: "delete", batch }) : undefined}
                  />
                  <div className="mt-2">
                    <BatchActionButtons
                      batch={batch}
                      canUpdate={canUpdate}
                      canDelete={canDelete}
                      canViewPayments={canViewPayments}
                      pdfLoadingId={pdfLoadingId}
                      onView={() => openBatchSalaries(batch.salaryBatchId)}
                      onPayments={() => openPaymentsModal(batch)}
                      onPdf={() => void downloadPdf(batch)}
                      onAction={openAction}
                    />
                  </div>
                </motion.div>
              );
            })}
          </div>
        ) : null}

        {!loading && filtered.length > 0 && viewMode === "list" ? (
          <div className="space-y-3">
            {paginated.map((batch, i) => {
              const badge = batchStatusBadge(batch.status);
              const updated = batch.updatedAt ? parseISO(batch.updatedAt) : null;
              return (
                <motion.div
                  key={batch.salaryBatchId}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.02 }}
                  className="glass-card p-3"
                >
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="h-14 w-14 rounded-md bg-primary/10 border border-border/50 shrink-0 flex items-center justify-center">
                      <Layers className="w-7 h-7 text-primary/70" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-sm font-semibold text-foreground truncate">{batchDisplayLabel(batch)}</h3>
                        <span className={cn("text-[11px] px-2 py-0.5 rounded-full font-medium", badge.cls)}>{badge.label}</span>
                        {batch.hasIssues ? (
                          <span className="text-[10px] font-medium text-amber-700 dark:text-amber-300">Has issues</span>
                        ) : null}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">
                        {batchListingSummary(batch)}
                        {batch.hasIssues ? " · Has issues" : ""}
                      </p>
                    </div>
                    <div className="text-right ml-auto shrink-0">
                      <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">{moneyWithCurrency(batch.netPayTotal)}</p>
                      <p className="text-[11px] text-muted-foreground">{batchAmountCaption(batch)}</p>
                      <p className="text-[11px] text-muted-foreground">{updated && isValid(updated) ? format(updated, "PP") : "—"}</p>
                    </div>
                    <div className="w-full sm:w-auto sm:ml-auto">
                      <BatchActionButtons
                        batch={batch}
                        canUpdate={canUpdate}
                        canDelete={canDelete}
                        canViewPayments={canViewPayments}
                        pdfLoadingId={pdfLoadingId}
                        onView={() => openBatchSalaries(batch.salaryBatchId)}
                        onPayments={() => openPaymentsModal(batch)}
                        onPdf={() => void downloadPdf(batch)}
                        onAction={openAction}
                      />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        ) : null}

        <GridPaginationFooter
          totalCount={totalCount}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </div>

      <Dialog
        open={
          Boolean(batchViewId) &&
          pendingSalaryAction?.type !== "pay" &&
          pendingSalaryAction?.type !== "pay_all"
        }
        onOpenChange={(open) => {
          if (!open) {
            // Pay dialog temporarily hides this dialog; keep the batch URL/state.
            if (pendingSalaryAction?.type === "pay" || pendingSalaryAction?.type === "pay_all") return;
            closeBatchSalaries();
          }
        }}
      >
        <DialogContent className="sm:max-w-7xl w-[98vw] max-h-[95vh] flex flex-col p-0 gap-0 overflow-hidden border-border/60 shadow-2xl">
          <div className="relative shrink-0 overflow-hidden border-b border-border/50 bg-gradient-to-br from-primary/[0.06] via-background to-emerald-500/[0.04]">
            <div className="relative px-4 pt-4 pb-3 space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <div className="h-9 w-9 rounded-xl bg-primary/10 border border-primary/15 flex items-center justify-center shrink-0">
                  <Layers className="h-4 w-4 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <DialogTitle className="text-lg font-display font-bold tracking-tight">
                      {viewBatch ? batchDisplayLabel(viewBatch) : "Batch salaries"}
                    </DialogTitle>
                    {viewBatch ? (
                      <span className={cn("text-[10px] font-semibold rounded-full px-2 py-0.5", batchStatusBadge(viewBatch.status).cls)}>
                        {batchStatusBadge(viewBatch.status).label}
                      </span>
                    ) : null}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-2.5">
                    <span className="inline-flex items-center gap-1">
                      <CalendarRange className="h-3 w-3" />
                      {viewBatch ? periodLabel(viewBatch.periodYear, viewBatch.periodMonth) : "Payroll period"}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      {batchSalaries.length} employees
                      {viewBatch ? (
                        <span className="text-muted-foreground/80">
                          {viewBatch.status === "paid"
                            ? " · All paid"
                            : viewBatch.status === "finalized"
                              ? viewBatch.paidCount > 0
                                ? ` · ${viewBatch.paidCount} paid · ${viewBatch.finalizedCount} awaiting payment`
                                : " · Finalized"
                              : ` · ${viewBatch.processedCount}/${viewBatch.employeesCount} processed`}
                        </span>
                      ) : null}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-0 bg-muted/[0.12]">
            <div className="flex items-center gap-2">
              <div className="relative flex-1 min-w-0">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <Input
                  className="glass-input pl-8 h-8 text-sm bg-background/80"
                  placeholder="Search name, code, department…"
                  value={salariesQuery}
                  onChange={(e) => setSalariesQuery(e.target.value)}
                />
              </div>
              {viewBatch && canViewPayments && viewBatch.status === "finalized" && viewBatch.finalizedCount > 0 ? (
                <AppTooltip label="Pay all finalized salaries in this batch">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 gap-1.5 shrink-0 bg-background/80"
                    disabled={salaryActionLoadingId === "__pay_all__"}
                    onClick={openBatchPayAllConfirm}
                  >
                    {salaryActionLoadingId === "__pay_all__" ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Wallet className="h-3.5 w-3.5" />
                    )}
                    Payments
                  </Button>
                </AppTooltip>
              ) : null}
              <AppTooltip label="Reload salaries for this batch">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0 shrink-0 bg-background/80"
                  disabled={batchSalariesLoading || !batchViewId}
                  onClick={() => batchViewId && void loadBatchSalaries(batchViewId)}
                >
                  <RefreshCw className={cn("h-3.5 w-3.5", batchSalariesLoading && "animate-spin")} />
                </Button>
              </AppTooltip>
            </div>

            <div className="rounded-xl border border-border/60 overflow-hidden bg-background shadow-sm">
              <div className="max-h-[min(68vh,36rem)] overflow-auto">
                <table className="w-full min-w-[60rem] text-xs">
                  <thead className="sticky top-0 z-10 bg-muted/95 backdrop-blur-sm">
                    <tr className="border-b border-border/50 text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
                      <th className="text-left font-semibold py-2 px-2.5">Employee</th>
                      <th className="text-left font-semibold py-2 px-2">Designation / Department</th>
                      <th className="text-center font-semibold py-2 px-2">Basic</th>
                      <th className="text-center font-semibold py-2 px-2">Additions</th>
                      <th className="text-center font-semibold py-2 px-2">Gross</th>
                      <th className="text-center font-semibold py-2 px-2">Deductions</th>
                      <th className="text-center font-semibold py-2 px-2">Net pay</th>
                      <th className="text-center font-semibold py-2 px-2">Status</th>
                      <th className="text-center font-semibold py-2 px-2 w-[4.5rem]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batchSalariesLoading ? (
                      <tr>
                        <td colSpan={9} className="py-12 text-center text-muted-foreground">
                          <div className="flex flex-col items-center justify-center gap-2">
                            <Loader2 className="h-6 w-6 animate-spin text-primary/70" />
                            <p className="text-xs">Loading salaries…</p>
                          </div>
                        </td>
                      </tr>
                    ) : filteredBatchSalaries.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="py-10 text-center">
                          <div className="flex flex-col items-center justify-center gap-2">
                            <Users className="h-6 w-6 text-muted-foreground/60" />
                            <p className="text-xs font-medium text-foreground">
                              {batchSalaries.length === 0 ? "No salaries in this batch" : "No matching employees"}
                            </p>
                            <p className="text-[11px] text-muted-foreground">
                              {batchSalaries.length === 0
                                ? "Process employees to populate this batch."
                                : "Try a different search."}
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredBatchSalaries.map((s, index) => {
                        const emp = employeeById.get(s.employeeId);
                        const displayName = s.employeeName || (emp ? employeeDisplayName(emp) : `Employee #${s.employeeId}`);
                        const code = emp?.employeeCode?.trim() || "";
                        const dept = emp?.departmentName?.trim() || "";
                        const designation = emp?.designationName?.trim() || "";
                        const basic = salaryBasic(s);
                        const additions = salaryAdditions(s);
                        const deductions = salaryDeductions(s);
                        const additionLines = salaryDetailLines(s, "additions");
                        const deductionLines = salaryDetailLines(s, "deductions");
                        const rowBusy = salaryActionLoadingId === s.id;
                        const pdfBusy = salaryPdfLoadingId === s.id;
                        const mutable = salaryCanMutate(s.status);
                        const rowOpen = salaryPopoverId === s.id;

                        return (
                          <tr
                            key={s.id}
                            className={cn(
                              "border-b border-border/40 last:border-0 transition-colors cursor-pointer select-none",
                              "hover:bg-primary/[0.05] active:bg-primary/[0.08]",
                              rowOpen && "bg-primary/[0.12] ring-1 ring-inset ring-primary/35 shadow-[inset_3px_0_0_0_hsl(var(--primary))]",
                              index % 2 === 1 && !rowOpen && "bg-muted/[0.18]",
                            )}
                            onClick={(e) => {
                              if ((e.target as HTMLElement).closest("[data-salary-row-action]")) return;
                              void openSalaryPopover(s);
                            }}
                          >
                            <td className="py-1.5 px-2.5 text-left align-middle">
                              <div className="flex items-center gap-2 min-w-0 max-w-[14rem]">
                                <div
                                  className={cn(
                                    "h-7 w-7 rounded-lg flex items-center justify-center text-[10px] font-bold ring-1 shrink-0",
                                    avatarToneForId(s.employeeId || s.id),
                                  )}
                                >
                                  {employeeInitials(displayName)}
                                </div>
                                <div className="min-w-0">
                                  <p className="text-xs font-semibold text-foreground leading-tight truncate">{displayName}</p>
                                  <p className="text-[10px] text-muted-foreground font-mono truncate">{code || "—"}</p>
                                </div>
                              </div>
                            </td>
                            <td className="py-1.5 px-2 text-left align-middle min-w-0 max-w-[12rem]">
                              <p className="text-[11px] text-foreground/90 truncate">{designation || "—"}</p>
                              <p className="text-[10px] text-muted-foreground truncate">{dept || "—"}</p>
                            </td>
                            <td className="py-1.5 px-2 text-center align-middle tabular-nums text-foreground/90">
                              {moneyWithCurrency(basic)}
                            </td>
                            <td className="py-1.5 px-2 text-center align-middle tabular-nums text-sky-700/90 dark:text-sky-300/90">
                              <SalaryAmountBreakdownTooltip kind="additions" lines={additionLines} total={additions}>
                                <span
                                  className={cn(
                                    "inline-block",
                                    additionLines.length > 0 && additions >= 0.005 && "underline decoration-dotted underline-offset-2 cursor-help",
                                  )}
                                >
                                  {moneyWithCurrency(additions)}
                                </span>
                              </SalaryAmountBreakdownTooltip>
                            </td>
                            <td className="py-1.5 px-2 text-center align-middle tabular-nums text-foreground/90">
                              {moneyWithCurrency(s.grossMonthlyTotal)}
                            </td>
                            <td className="py-1.5 px-2 text-center align-middle tabular-nums text-rose-700/90 dark:text-rose-300/90">
                              <SalaryAmountBreakdownTooltip kind="deductions" lines={deductionLines} total={deductions}>
                                <span
                                  className={cn(
                                    "inline-block",
                                    deductionLines.length > 0 && deductions >= 0.005 && "underline decoration-dotted underline-offset-2 cursor-help",
                                  )}
                                >
                                  {moneyWithCurrency(deductions)}
                                </span>
                              </SalaryAmountBreakdownTooltip>
                            </td>
                            <td className="py-1.5 px-2 text-center align-middle tabular-nums font-semibold text-emerald-700 dark:text-emerald-300">
                              <div className="leading-tight">
                                <p>{moneyWithCurrency(s.netPayTotal)}</p>
                                <p className="text-[10px] font-normal italic text-emerald-700/70 dark:text-emerald-300/70">
                                  {moneyWithCurrency(s.actualPay || s.netPayTotal)}
                                </p>
                              </div>
                            </td>
                            <td className="py-1.5 px-2 text-center align-middle">
                              <span
                                className={cn(
                                  "inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap",
                                  salaryStatusClass(s.status),
                                )}
                              >
                                {salaryStatusLabel(s.status)}
                              </span>
                            </td>
                            <td className="py-1.5 px-2 text-center align-middle" data-salary-row-action>
                              <div
                                className="inline-flex items-center justify-center rounded-lg border border-border/60 bg-muted/30 p-0.5 gap-1"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <AppTooltip label="Download salary slip PDF">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0 rounded-md text-foreground/75 hover:text-foreground hover:bg-background border border-transparent hover:border-border/60 transition-all"
                                    disabled={pdfBusy}
                                    onClick={() => void downloadSalaryPdfForRow(s)}
                                  >
                                    {pdfBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileDown className="w-3 h-3" />}
                                  </Button>
                                </AppTooltip>
                                {canViewPayments && viewBatch && s.status === "finalized" ? (
                                  <AppTooltip label="Pay this employee">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 w-7 p-0 rounded-md text-emerald-700 dark:text-emerald-400 hover:text-emerald-800 dark:hover:text-emerald-300 hover:bg-emerald-500/10 border border-transparent hover:border-emerald-500/20 transition-all"
                                      disabled={rowBusy}
                                      onClick={() => openSalaryPayConfirm(s)}
                                    >
                                      {rowBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wallet className="w-3 h-3" />}
                                    </Button>
                                  </AppTooltip>
                                ) : null}
                                {canDelete && mutable ? (
                                  <AppTooltip label="Delete this salary record">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 w-7 p-0 rounded-md text-rose-600 dark:text-rose-400 hover:text-rose-700 dark:hover:text-rose-300 hover:bg-rose-500/10 dark:hover:bg-rose-500/20 border border-transparent hover:border-rose-500/20 transition-all"
                                      disabled={rowBusy}
                                      onClick={() => openSalaryAction({ type: "delete", salary: s })}
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </AppTooltip>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                  {!batchSalariesLoading && filteredBatchSalaries.length > 0 ? (
                    <tfoot className="sticky bottom-0 z-10 bg-muted/95 backdrop-blur-sm">
                      <tr className="border-t border-border/60 text-[11px] font-semibold">
                        <td className="py-2 px-2.5 text-left align-middle text-muted-foreground" colSpan={2}>
                          Total
                          {salariesQuery.trim() ? (
                            <span className="ml-1 font-normal text-[10px]">(filtered)</span>
                          ) : null}
                        </td>
                        <td className="py-2 px-2 text-center align-middle tabular-nums text-foreground">
                          {moneyWithCurrency(filteredBatchSalaryTotals.basic)}
                        </td>
                        <td className="py-2 px-2 text-center align-middle tabular-nums text-sky-700 dark:text-sky-300">
                          {moneyWithCurrency(filteredBatchSalaryTotals.additions)}
                        </td>
                        <td className="py-2 px-2 text-center align-middle tabular-nums text-foreground">
                          {moneyWithCurrency(filteredBatchSalaryTotals.gross)}
                        </td>
                        <td className="py-2 px-2 text-center align-middle tabular-nums text-rose-700 dark:text-rose-300">
                          {moneyWithCurrency(filteredBatchSalaryTotals.deductions)}
                        </td>
                        <td className="py-2 px-2 text-center align-middle tabular-nums text-emerald-700 dark:text-emerald-300">
                          <div className="leading-tight">
                            <p>{moneyWithCurrency(filteredBatchSalaryTotals.netRounded5)}</p>
                            <p className="text-[10px] font-normal italic text-emerald-700/70 dark:text-emerald-300/70">
                              {moneyWithCurrency(filteredBatchSalaryTotals.net)}
                            </p>
                          </div>
                        </td>
                        <td className="py-2 px-2" />
                        <td className="py-2 px-2" />
                      </tr>
                    </tfoot>
                  ) : null}
                </table>
              </div>
            </div>
          </div>

          <DialogFooter className="px-4 py-2.5 border-t border-border/50 shrink-0 bg-background/80 flex-row items-center justify-between gap-2">
            <p className="text-[11px] text-muted-foreground">
              Showing <span className="font-medium text-foreground">{filteredBatchSalaries.length}</span> of{" "}
              <span className="font-medium text-foreground">{batchSalaries.length}</span>
            </p>
            <Button type="button" variant="glass" size="sm" className="h-8" onClick={closeBatchSalaries}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={salaryPopoverId != null}
        onOpenChange={(open) => {
          if (!open) closeSalaryPopover();
        }}
      >
        <DialogContent className="sm:max-w-5xl w-[96vw] max-h-[95vh] flex flex-col p-0 gap-0 overflow-hidden border-border/60 shadow-2xl z-[220]">
          {(() => {
            const listRow = batchSalaries.find((s) => s.id === salaryPopoverId) ?? null;
            const detail = salaryPopoverDetail ?? listRow;
            const emp = detail ? employeeById.get(detail.employeeId) : undefined;
            const displayName =
              detail?.employeeName ||
              (emp ? employeeDisplayName(emp) : detail ? `Employee #${detail.employeeId}` : "Employee");
            const pdfBusy = detail ? salaryPdfLoadingId === detail.id : false;

            return (
              <>
                <DialogHeader className="px-4 pt-4 pb-3 border-b border-border/50 shrink-0">
                  <DialogTitle className="text-lg font-display font-bold tracking-tight">
                    Salary detail — {displayName}
                  </DialogTitle>
                  <p className="text-xs text-muted-foreground">
                    Full breakdown including basic, attendance days, earnings, and deductions.
                  </p>
                </DialogHeader>
                <div className="flex-1 min-h-0 overflow-hidden">
                  <BatchSalaryDetailPanel
                    salary={salaryPopoverDetail}
                    employee={emp}
                    displayName={displayName}
                    loading={salaryPopoverLoading || !salaryPopoverDetail}
                    pdfBusy={pdfBusy}
                    onDownloadPdf={() => {
                      if (detail) void downloadSalaryPdfForRow(detail);
                    }}
                  />
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog
        open={processOpen}
        onOpenChange={(open) => {
          if (open) setProcessOpen(true);
          else closeProcessModal();
        }}
      >
        <DialogContent
          className={cn(
            "sm:max-w-4xl w-[95vw] max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden",
            batchViewId ? "z-[210]" : null,
          )}
        >
          <DialogHeader className="px-4 pt-4 pb-3 border-b border-border/50 shrink-0">
            <DialogTitle className="text-lg">{processMode === "payments" ? "Payments" : "Create batch"}</DialogTitle>
            <p className="text-xs text-muted-foreground">
              {processMode === "payments"
                ? "Select employees to pay from this batch."
                : "Set period and employees, then run payroll for the selected active staff."}
            </p>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-0">
            {processDataLoading ? (
              <div className="flex flex-col items-center justify-center gap-2 py-16 min-h-[18rem]">
                <Loader2 className="h-7 w-7 animate-spin text-primary/70" />
                <p className="text-xs text-muted-foreground">Loading…</p>
              </div>
            ) : (
            <>
            {processMode === "payments" ? (
            <LoanPaymentMethodFields
              compact
              value={paymentDraft}
              banks={banks}
              onChange={(patch) => setPaymentDraft((d) => ({ ...d, ...patch }))}
            />
            ) : (
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_5.5rem_8.5rem] gap-2 items-end">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Batch name</Label>
                <Input
                  className="glass-input h-8 text-sm"
                  value={processBatchName}
                  onChange={(e) => {
                    setProcessBatchNameEdited(true);
                    setProcessBatchName(e.target.value);
                  }}
                  placeholder="e.g. July 2026 payroll"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Year</Label>
                <Select
                  value={String(processYear)}
                  onValueChange={(v) => {
                    const y = Number(v);
                    setProcessYear(y);
                    if (!processBatchNameEdited) setProcessBatchName(defaultBatchName(y, processMonth));
                  }}
                >
                  <SelectTrigger className="glass-input h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {yearOptions.map((y) => (
                      <SelectItem key={y} value={String(y)}>
                        {y}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Month</Label>
                <Select
                  value={String(processMonth)}
                  onValueChange={(v) => {
                    const m = Number(v);
                    setProcessMonth(m);
                    if (!processBatchNameEdited) setProcessBatchName(defaultBatchName(processYear, m));
                  }}
                >
                  <SelectTrigger className="glass-input h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((m) => (
                      <SelectItem key={m.v} value={String(m.v)}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            )}

            <div className="space-y-2 flex flex-col min-h-0">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-[11px] text-muted-foreground">
                  <span className="font-semibold text-foreground">{processEmployeeIds.length}</span> selected ·{" "}
                  {filteredProcessEmployees.length} shown · {periodLabel(processYear, processMonth)}
                </p>
                <Button type="button" variant="outline" size="sm" className="h-7 text-[11px] px-2.5" onClick={toggleAllProcessEmployees}>
                  {allFilteredEmployeesSelected ? "Deselect all" : "Select all"}
                  {processEmployeeQuery.trim() || processDepartmentId !== "__all__" ? " (filtered)" : ""}
                </Button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-[1fr_11rem] gap-2">
                <div className="relative min-w-0">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                  <Input
                    className="glass-input pl-8 h-8 text-sm"
                    placeholder="Search name, code, department, designation…"
                    value={processEmployeeQuery}
                    onChange={(e) => setProcessEmployeeQuery(e.target.value)}
                  />
                </div>
                <Select value={processDepartmentId} onValueChange={setProcessDepartmentId}>
                  <SelectTrigger className="glass-input h-8 text-sm">
                    <SelectValue placeholder="All departments" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All departments</SelectItem>
                    {departments.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="rounded-lg border border-border/60 overflow-hidden bg-background shadow-sm">
                <div className="max-h-[min(50vh,24rem)] overflow-auto">
                  <table className="w-full min-w-[28rem] text-xs">
                    <thead className="sticky top-0 z-10 bg-muted/95 backdrop-blur-sm">
                      <tr className="border-b border-border/50 text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
                        <th className="text-left font-semibold py-2 px-2.5">Employee</th>
                        <th className="text-left font-semibold py-2 px-2">Designation / Department</th>
                        <th className={cn("font-semibold py-2 px-2", processMode === "payments" ? "text-right" : "text-center")}>
                          {processMode === "payments" ? "Amount" : "Type"}
                        </th>
                        {processMode === "payments" ? (
                          <th className="text-center font-semibold py-2 px-2">Status</th>
                        ) : null}
                      </tr>
                    </thead>
                    <tbody>
                      {employees.length === 0 ? (
                        <tr>
                          <td colSpan={processMode === "payments" ? 4 : 3} className="py-8 text-center text-muted-foreground text-xs">
                            No employees loaded.
                          </td>
                        </tr>
                      ) : filteredProcessEmployees.length === 0 ? (
                        <tr>
                          <td colSpan={processMode === "payments" ? 4 : 3} className="py-8 text-center text-muted-foreground text-xs">
                            {processMode === "payments"
                              ? "No finalized salaries in this batch match your filters."
                              : "No employees match your filters."}
                          </td>
                        </tr>
                      ) : (
                        filteredProcessEmployees.map((e, index) => {
                          const selected = processEmployeeIds.includes(e.id);
                          const name = employeeDisplayName(e);
                          const code = e.employeeCode?.trim() || "";
                          const dept = e.departmentName?.trim() || "";
                          const designation = e.designationName?.trim() || "";
                          const paymentSalary =
                            processMode === "payments"
                              ? paymentSalaries.find((s) => s.employeeId === e.id && s.status === "finalized")
                              : undefined;
                          return (
                            <tr
                              key={e.id}
                              role="button"
                              tabIndex={0}
                              aria-pressed={selected}
                              onClick={() => toggleProcessEmployee(e.id)}
                              onKeyDown={(ev) => {
                                if (ev.key === "Enter" || ev.key === " ") {
                                  ev.preventDefault();
                                  toggleProcessEmployee(e.id);
                                }
                              }}
                              className={cn(
                                "border-b border-border/40 last:border-0 transition-colors select-none",
                                "cursor-pointer hover:bg-primary/[0.05] active:bg-primary/[0.08]",
                                selected && "bg-primary/[0.1] ring-1 ring-inset ring-primary/30 hover:bg-primary/[0.12] shadow-[inset_3px_0_0_0_hsl(var(--primary))]",
                                index % 2 === 1 && !selected && "bg-muted/[0.12]",
                              )}
                            >
                              <td className="py-1.5 px-2.5 text-left align-middle">
                                <div className="flex items-center gap-2 min-w-0 max-w-[14rem]">
                                  <div
                                    className={cn(
                                      "h-7 w-7 rounded-lg flex items-center justify-center text-[10px] font-bold ring-1 shrink-0",
                                      avatarToneForId(e.id),
                                      selected && "ring-primary/50",
                                    )}
                                  >
                                    {employeeInitials(name)}
                                  </div>
                                  <div className="min-w-0">
                                    <p
                                      className={cn(
                                        "text-xs font-semibold leading-tight truncate",
                                        selected ? "text-primary" : "text-foreground",
                                      )}
                                    >
                                      {name}
                                    </p>
                                    <p className="text-[10px] text-muted-foreground font-mono truncate">
                                      {code || "—"}
                                    </p>
                                  </div>
                                </div>
                              </td>
                              <td className="py-1.5 px-2 text-left align-middle min-w-0 max-w-[12rem]">
                                <p className="text-[11px] text-foreground/90 truncate">{designation || "—"}</p>
                                <p className="text-[10px] text-muted-foreground truncate">{dept || "—"}</p>
                              </td>
                              <td
                                className={cn(
                                  "py-1.5 px-2 align-middle text-[11px] max-w-[7.5rem]",
                                  processMode === "payments"
                                    ? "text-right tabular-nums font-semibold text-emerald-700 dark:text-emerald-300"
                                    : "text-center text-muted-foreground truncate",
                                )}
                              >
                                {processMode === "payments" ? (
                                  paymentSalary ? (
                                    <div className="leading-tight">
                                      <p>{moneyWithCurrency(paymentSalary.netPayTotal)}</p>
                                      <p className="text-[10px] font-normal italic text-emerald-700/70 dark:text-emerald-300/70">
                                        {moneyWithCurrency(paymentSalary.actualPay || paymentSalary.netPayTotal)}
                                      </p>
                                    </div>
                                  ) : (
                                    "—"
                                  )
                                ) : (
                                  e.employeeType || "—"
                                )}
                              </td>
                              {processMode === "payments" ? (
                                <td className="py-1.5 px-2 text-center align-middle">
                                  {paymentSalary ? (
                                    <span
                                      className={cn(
                                        "inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap",
                                        salaryStatusClass(paymentSalary.status),
                                      )}
                                    >
                                      {salaryStatusLabel(paymentSalary.status)}
                                    </span>
                                  ) : (
                                    "—"
                                  )}
                                </td>
                              ) : null}
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {processEmployeeIds.length === 0 ? (
                <p className="text-[11px] text-amber-700 dark:text-amber-300 font-medium">Select at least one employee.</p>
              ) : null}
            </div>
            </>
            )}
          </div>

          <DialogFooter className="px-4 py-2.5 border-t border-border/50 shrink-0 bg-muted/15 gap-2">
            <Button type="button" variant="glass" size="sm" className="h-8" onClick={closeProcessModal} disabled={processLoading}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="premium"
              size="sm"
              className="gap-1.5 h-8"
              disabled={
                processLoading ||
                processDataLoading ||
                processEmployeeIds.length === 0 ||
                (processMode !== "payments" && !processBatchName.trim()) ||
                (processMode === "payments" && !canCreatePayments)
              }
              onClick={() => void runProcessBatch()}
            >
              {processLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
              {processMode === "payments" ? `Pay (${processEmployeeIds.length})` : `Create batch (${processEmployeeIds.length})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmAlertDialog
        open={pendingSalaryAction?.type === "delete"}
        onOpenChange={(open) => !open && setPendingSalaryAction(null)}
        title="Delete this salary?"
        description={
          pendingSalaryAction?.type === "delete" ? (
            <>
              Delete salary for{" "}
              <strong>
                {pendingSalaryAction.salary.employeeName || `Employee #${pendingSalaryAction.salary.employeeId}`}
              </strong>
              ? This cannot be undone.
            </>
          ) : (
            ""
          )
        }
        mode="confirm"
        variant="danger"
        confirmLabel="Delete"
        onConfirm={() => void runPendingSalaryAction()}
      />

      <Dialog
        open={pendingSalaryAction?.type === "pay" || pendingSalaryAction?.type === "pay_all"}
        onOpenChange={(open) => {
          if (!open) {
            setPendingSalaryAction(null);
            setPaymentDraft(emptyLoanPaymentDraft());
          }
        }}
      >
        <DialogContent
          className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto p-6 z-[210]"
          overlayClassName="z-[200]"
          onPointerDownOutside={(e) => {
            // Keep pay dialog open when interacting with portaled Select/Popover menus.
            const target = e.target as HTMLElement | null;
            if (target?.closest("[data-radix-select-content], [data-radix-popper-content-wrapper], [role='listbox']")) {
              e.preventDefault();
            }
          }}
          onInteractOutside={(e) => {
            const target = e.target as HTMLElement | null;
            if (target?.closest("[data-radix-select-content], [data-radix-popper-content-wrapper], [role='listbox']")) {
              e.preventDefault();
            }
          }}
        >
          {(() => {
            const isPayAll = pendingSalaryAction?.type === "pay_all";
            const paySalary = pendingSalaryAction?.type === "pay" ? pendingSalaryAction.salary : null;
            const finalizedRows = batchSalaries.filter((s) => s.status === "finalized");
            const actualNet = paySalary
              ? parseMoney(paySalary.actualPay || paySalary.netPayTotal)
              : finalizedRows.reduce((sum, s) => sum + parseMoney(s.actualPay || s.netPayTotal), 0);
            const roundedNet = paySalary
              ? parseMoney(paySalary.netPayTotal)
              : finalizedRows.reduce((sum, s) => sum + parseMoney(s.netPayTotal), 0);
            const employeeName = paySalary
              ? paySalary.employeeName || `Employee #${paySalary.employeeId}`
              : null;
            const payBusy =
              salaryActionLoadingId === (paySalary?.id ?? "__pay_all__") ||
              salaryActionLoadingId === "__pay_all__";

            return (
              <>
                <DialogHeader className="pb-3 border-b border-border/50">
                  <div className="flex items-center gap-2">
                    <div className="h-9 w-9 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-600">
                      <Wallet className="w-5 h-5" />
                    </div>
                    <div>
                      <DialogTitle className="text-lg font-bold text-foreground">
                        {isPayAll ? "Pay finalized salaries" : "Pay salary"}
                      </DialogTitle>
                      <p className="text-xs text-muted-foreground">
                        {isPayAll
                          ? "Record payment for all finalized employees in this batch."
                          : "Record payment for this employee’s finalized salary."}
                      </p>
                    </div>
                  </div>
                </DialogHeader>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-1">
                  <div className="flex flex-col justify-between rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-3">
                    <div className="flex items-center gap-2 pb-2 border-b border-primary/10">
                      <Wallet className="w-4 h-4 text-primary" />
                      <h4 className="font-bold text-xs uppercase tracking-wider text-foreground">Payment summary</h4>
                    </div>
                    <div className="space-y-2.5 text-xs">
                      {employeeName ? (
                        <div className="flex justify-between items-center gap-3">
                          <span className="text-muted-foreground">Employee</span>
                          <span className="font-semibold text-foreground truncate text-right">{employeeName}</span>
                        </div>
                      ) : (
                        <div className="flex justify-between items-center gap-3">
                          <span className="text-muted-foreground">Employees</span>
                          <span className="font-semibold text-foreground">{finalizedRows.length} finalized</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center gap-3">
                        <span className="text-muted-foreground">Actual net</span>
                        <span className="font-bold tabular-nums text-foreground">{moneyWithCurrency(actualNet)}</span>
                      </div>
                      <div className="flex justify-between items-center gap-3 border-t border-primary/10 pt-2">
                        <span className="text-muted-foreground">Payable (round 5)</span>
                        <div className="text-right">
                          <p className="font-extrabold tabular-nums text-emerald-700 dark:text-emerald-300 text-sm">
                            {moneyWithCurrency(roundedNet)}
                          </p>
                          <p className="text-[10px] italic text-muted-foreground">Nearest Rs. 5</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground pt-2 border-t border-primary/10">
                      <Info className="w-3.5 h-3.5 text-primary shrink-0" />
                      <span>Paid amount uses net pay (nearest Rs. 5). Actual pay is the exact calculated amount.</span>
                    </div>
                  </div>

                  <div className="rounded-xl border border-border/50 bg-muted/15 p-4 space-y-3">
                    <p className="font-bold text-xs uppercase tracking-wider text-foreground">Payment method</p>
                    <LoanPaymentMethodFields
                      compact
                      stacked
                      value={paymentDraft}
                      banks={banks}
                      onChange={(patch) => setPaymentDraft((d) => ({ ...d, ...patch }))}
                    />
                  </div>
                </div>

                <DialogFooter className="pt-4 border-t border-border/50 gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={payBusy}
                    onClick={() => {
                      setPendingSalaryAction(null);
                      setPaymentDraft(emptyLoanPaymentDraft());
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    disabled={payBusy || !canCreatePayments}
                    className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-5"
                    onClick={() => {
                      if (!canCreatePayments) {
                        toast.error("You don’t have permission to record salary payments.");
                        return;
                      }
                      const paymentError = validateLoanPayment(paymentDraft);
                      if (paymentError) {
                        toast.error(paymentError);
                        return;
                      }
                      void runPendingSalaryAction();
                    }}
                  >
                    {payBusy ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Paying...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4" />
                        {isPayAll ? `Pay ${finalizedRows.length}` : "Pay"}
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      <ConfirmAlertDialog
        open={pendingAction?.type === "regenerate"}
        onOpenChange={(open) => !open && setPendingAction(null)}
        title="Regenerate this batch?"
        description={
          pendingAction?.type === "regenerate" ? (
            <>
              Recalculate all non-finalized salaries in batch <strong>{batchDisplayLabel(pendingAction.batch)}</strong>,
              and re-add any active employees for this period who were removed from the batch.
              Finalized or paid salaries are left unchanged.
            </>
          ) : (
            ""
          )
        }
        mode="confirm"
        variant="warning"
        confirmLabel="Regenerate"
        onConfirm={() => void runPendingAction()}
      />

      <ConfirmAlertDialog
        open={pendingAction?.type === "finalize"}
        onOpenChange={(open) => !open && setPendingAction(null)}
        title="Finalize this batch?"
        description={
          pendingAction?.type === "finalize" ? (
            <>
              Lock all draft salaries in batch <strong>{batchDisplayLabel(pendingAction.batch)}</strong>? Finalized salaries cannot be
              reprocessed.
            </>
          ) : (
            ""
          )
        }
        mode="confirm"
        variant="warning"
        confirmLabel="Finalize"
        onConfirm={() => void runPendingAction()}
      />

      <ConfirmAlertDialog
        open={pendingAction?.type === "delete"}
        onOpenChange={(open) => !open && setPendingAction(null)}
        title="Delete this batch?"
        description={
          pendingAction?.type === "delete" ? (
            <>
              Delete batch <strong>{batchDisplayLabel(pendingAction.batch)}</strong> and unlink its salaries? This cannot be undone.
            </>
          ) : (
            ""
          )
        }
        mode="confirm"
        variant="danger"
        confirmLabel="Delete"
        onConfirm={() => void runPendingAction()}
      />

      <Dialog
        open={pendingAction?.type === "exclude" || pendingAction?.type === "revoke"}
        onOpenChange={(open) => !open && setPendingAction(null)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{pendingAction?.type === "revoke" ? "Revoke batch" : "Exclude batch"}</DialogTitle>
          </DialogHeader>
          {pendingAction && (pendingAction.type === "exclude" || pendingAction.type === "revoke") ? (
            <div className="space-y-4 py-1">
              <p className="text-sm text-muted-foreground">
                {pendingAction.type === "revoke" ? "Revoke" : "Exclude"} all eligible salaries in batch{" "}
                <strong>{batchDisplayLabel(pendingAction.batch)}</strong>.
              </p>
              <div className="space-y-1.5">
                <Label>Reason (optional)</Label>
                <Input className="glass-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Payroll hold" />
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="glass" onClick={() => setPendingAction(null)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button type="button" variant="premium" disabled={actionLoading} onClick={() => void runPendingAction()}>
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : pendingAction?.type === "revoke" ? "Revoke" : "Exclude"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
